/** Labels and formatting for the printed pay sheet. Every label is English + Hindi (owner's ask). */

export const L = {
  title: ["Pay sheet", "मज़दूरी भुगतान पत्रक"],
  dates: ["Dates", "तारीख़"],
  code: ["Code", "कोड"],
  name: ["Name", "नाम"],
  total: ["Total", "कुल"],
  earlier: ["Earlier due", "पिछला बकाया"],
  alreadyPaid: ["already paid", "पहले दिया"],
  notCounted: ["not counted", "गिना नहीं"],
  stillIn: ["still in", "अभी काम पर"],
  advance: ["Advance owed", "बाकी अग्रिम"],
  cut: ["Advance cut", "अग्रिम कटौती"],
  net: ["Net to pay", "देय राशि"],
  sign: ["Signature / thumb", "हस्ताक्षर / अंगूठा"],
  supervisor: ["Supervisor", "पर्यवेक्षक"],
  paid: ["Paid", "भुगतान हुआ"],
  sheetTotal: ["Sheet total", "कुल योग"],
  printed: ["Printed", "छपा"],
  correction: ["Correction", "सुधार"],
  toRecover: ["To recover", "वसूली बाकी"],
  taken: ["Taken back now", "इस बार वसूला"],
  carried: ["Left for later sheets", "अगले पत्रकों में"],
  draft: ["DRAFT: not for payment", "ड्राफ़्ट: भुगतान के लिए नहीं"],
  discarded: ["DISCARDED: not for payment", "हटाया गया: भुगतान के लिए नहीं"],
} as const;

/**
 * A draft sheet (saved by the AI assistant) is only checked and printed: nobody is paid from it until the owner
 * finalises it. Sheets from before drafts have no status and are final.
 */
export const isDraftSheet = (sheet: any): boolean => sheet?.status === "DRAFT";

/** A draft the owner threw away: listed nowhere, but an old link still opens it. Never paid from, never refreshed. */
export const isDiscardedSheet = (sheet: any): boolean => sheet?.status === "DISCARDED";

/** The line printed on a sheet nobody may be paid from (draft or discarded); null for a final sheet. */
export const notForPaymentLabel = (sheet: any): readonly [string, string] | null =>
  isDiscardedSheet(sheet) ? L.discarded : isDraftSheet(sheet) ? L.draft : null;

/** Paid work removed since the person's last paid line (already inside wages due; this labels it). */
export type PayCorrection = {
  entryId?: string;
  date?: string;
  sareeNumber?: string;
  loomCode?: string;
  workTypeCode?: string;
  inches?: number | string;
  paidSheetLabel?: string;
  amount?: string | number;
  reason?: string;
  byName?: string;
  label?: string;
  /** Stable id of this removal (entry + removal time) and what is still to recover from it. */
  key?: string;
  left?: string | number;
};

/** What is still left to recover from this correction, when part of it was already taken back on earlier sheets. */
export const correctionLeft = (c: PayCorrection): number | null => {
  if (c.left == null || c.left === "") return null;
  const left = Math.abs(Number(c.left));
  const full = Math.abs(Number(c.amount || 0));
  return Number.isFinite(left) && Math.abs(left - full) > 0.004 ? left : null;
};

/** What the correction is, from the server's label: "Removed:", "Moved, paid less:" or "Corrected, paid less:". */
export const correctionKind = (c: PayCorrection): "REMOVED" | "MOVED" | "CORRECTED" => {
  const l = (c.label || "").trim();
  if (l.startsWith("Moved")) return "MOVED";
  if (l.startsWith("Corrected")) return "CORRECTED";
  return "REMOVED";
};

export const CORRECTION_TITLE = {
  REMOVED: ["Removed: {{what}}", "Removed", "हटाया गया"],
  MOVED: ["Moved, paid less: {{what}}", "Moved, paid less", "बदला गया, कम भुगतान"],
  CORRECTED: ["Corrected, paid less: {{what}}", "Corrected, paid less", "सुधारा गया, कम भुगतान"],
} as const;

/** The correction balance on a line: before this sheet, taken back now, left for later sheets. */
export const correctionBalance = (line: any): { outstanding: number; taken: number; carried: number } => ({
  outstanding: Number(line?.correctionOutstanding) || 0,
  taken: Number(line?.correctionTaken) || 0,
  carried: Number(line?.correctionCarried) || 0,
});

/** "19 Oct 2026, saree 18350, 14 in S_1, paid on 26 Oct 2026 – 1 Nov 2026"; the server's label when the parts are missing. */
export const correctionWhat = (c: PayCorrection, fmtDay: (iso: string) => string, fmtRange: (s: string) => string): string => {
  if (!c.date) return c.label || "";
  const work = [c.inches !== undefined && c.inches !== null && c.inches !== "" ? `${c.inches} in` : "", c.workTypeCode || ""].filter(Boolean).join(" ");
  return [fmtDay(c.date), c.sareeNumber ? `saree ${c.sareeNumber}` : "", work, c.paidSheetLabel ? `paid on ${fmtRange(c.paidSheetLabel)}` : ""]
    .filter(Boolean)
    .join(", ");
};

const EN_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HI_DAYS = ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"];

/** "2026-10-17" → { day: "17/10", en: "Sat", hi: "शनि" } */
export const dayHead = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return {
    day: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`,
    en: EN_DAYS[d.getDay()],
    hi: HI_DAYS[d.getDay()],
  };
};

/** Whole rupees with Indian grouping for print; blank for nothing. */
export const amt = (v?: string | number | null): string => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!isFinite(n) || n === 0) return "";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** The 7 days ending today, the usual weekly sheet (today's work is often what is being paid). */
export const defaultRange = (today: Date = new Date()): { from: string; to: string } => {
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const to = new Date(today);
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  return { from: iso(from), to: iso(to) };
};

/** Quick picks for the sheet's dates (Figma 5c): this week = the 7 days ending today, last week = the 7 before. */
export const quickRanges = (today: Date = new Date()): { key: string; label: string; from: string; to: string }[] => {
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const week = defaultRange(today);
  const lastTo = new Date(`${week.from}T00:00:00`);
  lastTo.setDate(lastTo.getDate() - 1);
  return [
    { key: "this", label: "This week", ...week },
    { key: "last", label: "Last week", ...defaultRange(lastTo), to: iso(lastTo) },
  ];
};

/** One person's line in a few words: inches woven, days worked, hours of time work, advance cut. */
export const lineSummary = (line: any): { inches: number; days: number; minutes: number; cut: number } => {
  let inches = 0;
  let minutes = 0;
  let days = 0;
  for (const cell of line?.days || []) {
    const work: string[] = cell.work || [];
    if (work.length) days++;
    for (const w of work) {
      const inch = w.match(/([\d.]+) in$/);
      if (inch) inches += Number(inch[1]);
      const time = w.match(/^Time (\d+):(\d{2}) h$/);
      if (time) minutes += Number(time[1]) * 60 + Number(time[2]);
    }
  }
  return { inches: Math.round(inches * 100) / 100, days, minutes, cut: Number(line?.cut) || 0 };
};
